/**
 * Debounce a function.
 * Useful to slow down the event handling of fast and often called events.
 *
 * @param {Function} fn - The function to be called after debouncing.
 * @param {Number=} ms - The duration [millisecond] how long to wait.
 * @param {Boolean=} immediate - If true, call the function and ignore calls for the given duration (acts as lock).
 *                               If false, wait for given duration and then call the function.
 *                               If in the meantime a new call appears, wait the complete time from begin.
 * @return {Function} A new function.
 */
export function debounce(fn, ms = 0, immediate = false) {
	let timer;
	return function (...args) {
		if (immediate) {
			if (!timer) {
				timer = setTimeout(() => {
					timer = null
				}, ms);
				fn.apply(this, args);
			}
		} else {
			clearTimeout(timer)
			timer = setTimeout(fn.bind(this, ...args), ms)
		}
	}
}

export function scrollToTarget(element, offset = 0) {
	const elementPosition = element.getBoundingClientRect().top;
	window.scrollBy({
		top: elementPosition + offset,
		behavior: 'smooth',
	});
}

export function addEventListenerIfExist(name, handler, ...elements) {
	return elements.map(element => {
		if (typeof element === 'string' || element instanceof String) {
			return addEventListenerIfExist(name, handler, ...document.querySelectorAll(element))
		}
		if (!element) return null;
		return element.addEventListener(name, handler);
	});
}

/**
 * Add an event handler for all elements which match
 * the current selector, now and in the future.

 * This function adds an eventListener to the container
 * and call the handler when the event hit the element
 * (or a children) of the selector.
 *
 * `this` from the handler will be bound to the element
 * selected by the given selector.
 *
 * @param {String} name The event type to listen for.
 * @param {String} elementQuerySelector A selector for the element(s) which should be
 * @param {Function} handler The function which should be called.
 * @param {Node=} container The container which should observe element.
 */
export function addLiveEventListener(name, elementQuerySelector, handler, container = document) {
	return container.addEventListener(name, e => {
		const element = e.target.closest(elementQuerySelector);
		if (element) {
			return handler.call(element, e);
		}
		return false;
	});
}

export function getUrlVars() {
	return new URLSearchParams(window.location.search);
}

export function getUrlVar(name) {
	return getUrlVars().get(name);
}


export function formatPrice(price) {
	return parseFloat(price).toLocaleString('de-DE', {
		style: 'currency',
		currency: 'EUR',
		minimumFractionDigits: 2
	});
}

export function waitForTrans(cb) {
	// TODO: not the best solution...
	if (app.info.translations === undefined)
		return setTimeout(function () {
			waitForTrans(cb)
		}, 50);

	return cb();
}

/**
 * returns the amount of pixels for a single em
 * @source: https://stackoverflow.com/a/49510926/3749896
 */
export function getSingleEmInPixels() {
	let low = 0;
	let high = 200;
	let emWidth = Math.round((high - low) / 2) + low;
	const time = performance.now();
	let iters = 0;
	const maxIters = 10;
	while (high - low > 1) {
		const match = window.matchMedia(`(min-width: ${emWidth}em)`).matches;
		iters += 1;
		if (match) {
			low = emWidth;
		} else {
			high = emWidth;
		}
		emWidth = Math.round((high - low) / 2) + low;
		if (iters > maxIters) {
			console.warn(`max iterations reached ${iters}`);
			break;
		}
	}
	const singleEmPx = Math.ceil(window.innerWidth / emWidth);
	console.log(`window em width = ${emWidth}, time elapsed =  ${(performance.now() - time)}ms`);
	return singleEmPx;
}


/**
 * Like DOMTokenList.toggle()
 * @link https://developer.mozilla.org/en-US/docs/Web/API/DOMTokenList/toggle
 * @param {Array} array The array
 * @param {Object} item An element you want to toggle.
 * @param {boolean=} force If included, turns the toggle into a one way-only operation.
 * 	If set to false, then token will only be removed, but not added.
 * 	If set to true, then token will only be added, but not removed.
 *
 * 	Warning: may not work as expected for arrays which contains the element multiple times
 */
export function toggle(array, item, force = null) {
	const index = array.indexOf(item);
	console.debug('toogle(%o, %s, %s) --> %s', [...array], item, force,
		force === true || !~index && force !== false);
	if (force === true || (force !== false && !~index)) {
		array.push(item);
	} else if (force === false || (force !== true && ~index)) {
		array.splice(index, 1);
	}
	return force === true || (!~index && force !== false);
}

/**
 * Preload a ressource.
 *
 * @param {String} url The ressource URL (must contain the same query-string.)
 * @param {String} rel Type of preload: preload, prefetch, preconnect, ...
 * @param {String} as Type of usage: fetch, script, ...
 * @returns {Promise<Event>} The event from onload or onerror.
 */
export function preload({url, rel = 'preload', as = 'fetch'}) {
	return new Promise((resolve, reject) => {
		const node = document.createElement('link');
		node.rel = rel;
		node.as = as;
		node.href = url;
		node.onload = resolve;
		node.onerror = reject;
		document.head.appendChild(node);
	});
}
